import { auth } from "../firebaseConfig";
import store from "../store";
import { companyActions } from "../store/company-slice";
import { removeUser, userActions } from "../store/user-slice";
import { fetchMe } from "./http/users";

import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
} from "@firebase/auth";
import { requestMagicLink } from "./http/users";

const isDevelopment = process.env.NODE_ENV == "development";
const provider = new GoogleAuthProvider();

const MAGIC_LINK_EMAIL_KEY = "magicLinkEmail";

export function listenToAuthStateChanges() {
  auth.onAuthStateChanged(async (user) => {
    if (user) {
      const idToken = await user.getIdToken();
      try {
        // Fetch full user info from backend
        const userInfo = await fetchMe({ token: idToken });
        store.dispatch(
          userActions.setUser({
            email: userInfo.email,
            displayName: userInfo.display_name,
            roles: userInfo.roles,
          })
        );
      } catch (error) {
        console.error("Error fetching user info:", error);
        // Fallback to Firebase data if /me endpoint fails
        store.dispatch(
          userActions.setUser({
            email: user.email,
            displayName: user.displayName,
            roles: [],
          })
        );
      }
    } else {
      store.dispatch(removeUser());
      store.dispatch(companyActions.setActiveCompany(null));
    }
  });
}

export const signInWithGoogle = async () => {
  try {
    if (isDevelopment) {
      await signInWithPopup(auth, provider);
    } else {
      await signInWithRedirect(auth, provider);
    }
  } catch (error) {
    console.log(error);
  }
};

/**
 * Ask the backend to email a passwordless magic-link sign-in link.
 *
 * The email is remembered locally so the sign-in can be completed without
 * re-prompting when the user returns via the link (see completeMagicLinkSignIn).
 */
export const sendMagicLink = async (email: string): Promise<void> => {
  await requestMagicLink({ email });
  window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
};

/**
 * If the current URL is a Firebase email sign-in link, complete the sign-in.
 *
 * Returns true if a sign-in was attempted (regardless of outcome). Should be
 * called once on app load, before/alongside the auth-state listener.
 */
export const completeMagicLinkSignIn = async (): Promise<boolean> => {
  if (!isSignInWithEmailLink(auth, window.location.href)) {
    return false;
  }

  // The email is required to complete sign-in. Prefer the value we stored when
  // sending the link; otherwise read it from the URL fragment (Compass includes
  // it in the continueUrl so links opened in a new browser Just Work); fall
  // back to prompting only as a last resort.
  let email = window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
  if (!email) {
    email = new URLSearchParams(window.location.hash.slice(1)).get("email");
  }
  if (!email) {
    email = window.prompt("Please confirm the email address you used to request the magic link.");
  }
  if (!email) {
    return false;
  }

  try {
    await signInWithEmailLink(auth, email, window.location.href);
    window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
    // Strip the one-time sign-in params from the URL.
    window.history.replaceState({}, document.title, window.location.pathname);
  } catch (error) {
    console.error("Error completing magic-link sign-in:", error);
  }
  return true;
};
